import { useMemo } from 'react';

import { Option as AccessOption } from '@/react/portainer/access-control/AccessManagement/PorAccessManagementUsersSelector';
import { RegistrySelector } from '@/react/portainer/policies/components/RegistrySelector/RegistrySelector';

import type { Option as PortainerOption } from '@@/form-components/PortainerSelect';
import { actionsColumn } from '@@/datatables/editable/actionsColumn';
import { NEW_ROW_ID } from '@@/datatables/editable/EditableDatatable';
import { Link } from '@@/Link';

import { createAccessColumn } from './access';
import { columnHelper, type AccessRow } from './helper';

interface UseColumnsProps {
  accessRows: AccessRow[];
  onChange: (value: AccessRow[]) => void;
  userTeamOptions: PortainerOption<AccessOption>[];
  users: AccessOption[];
  teams: AccessOption[];
  handleAccessChange: (
    item: AccessRow,
    selection: readonly AccessOption[]
  ) => void;
  onRemove: (item: AccessRow) => void;
}

export function useColumns({
  accessRows,
  onChange,
  userTeamOptions,
  users,
  teams,
  handleAccessChange,
  onRemove,
}: UseColumnsProps) {
  return useMemo(() => {
    return [
      columnHelper.accessor('registryName', {
        header: 'Registry',
        cell: ({ row: { original } }) => {
          if (original.id === NEW_ROW_ID) {
            return (
              <RegistrySelector
                value={original.registryId}
                onChange={(val) => handleRegistryChange(original, val)}
                filter={(registries) => registries.filter((r) => r.Id !== 0)}
              />
            );
          }

          return (
            <Link
              to={`/admin/registries/${original.registryId}`}
              data-cy={`registry-link-${original.registryId}`}
            >
              {original.registryName || 'Unknown'}
            </Link>
          );
        },
        meta: {
          className: 'w-1/3',
        },
      }),
      createAccessColumn({
        userTeamOptions,
        users,
        teams,
        handleAccessChange,
      }),
      actionsColumn<AccessRow>(
        onRemove,
        (row: AccessRow) => row.id === NEW_ROW_ID
      ),
    ];

    function handleRegistryChange(item: AccessRow, registryId: number | null) {
      updateAccess(item, (prev) => ({
        ...prev,
        registryId,
      }));
    }

    function updateAccess(
      item: AccessRow,
      updater: (prev: AccessRow) => AccessRow
    ) {
      const newAccessRow = [...accessRows];
      const index = newAccessRow.findIndex(
        (access) => access.registryId === item.registryId
      );
      if (index !== -1) {
        newAccessRow[index] = updater(newAccessRow[index]);
        onChange(newAccessRow);
      }
    }
  }, [
    userTeamOptions,
    users,
    teams,
    handleAccessChange,
    onRemove,
    accessRows,
    onChange,
  ]);
}
